import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { POPUP } from "../brand";
import { tokens } from "../theme";
import { pillSx } from "./ui";
import { useCart } from "./CartContext";
import { FALLBACK_PRODUCTS } from "../data/biomarkers";
import { money } from "../data/site";
import { getProducts } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";

const KEY = "veyso_popup_seen";
const seen = () => { try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; } };
const remember = () => { try { sessionStorage.setItem(KEY, "1"); } catch { /* private mode: it may show again */ } };

/**
 * Opens once per visit when the visitor has scrolled about halfway below the hero
 * (POPUP.afterHeroFactor x the hero height). Shows three products with prices; the visitor can add one
 * to the cart or close it with the X. Product choice is sample content until VEYSO picks the "exclusive" items.
 */
export default function ScrollPopup() {
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState({});
  const { add } = useCart();
  const { data } = useApiData(() => getProducts("ADD_ON"), FALLBACK_PRODUCTS.filter((p) => p.type === "ADD_ON"));
  const products = (data || []).slice(0, 3);

  useEffect(() => {
    if (seen()) return undefined;
    const onScroll = () => {
      const hero = document.getElementById("home-hero");
      const height = hero ? hero.offsetHeight : 800;
      if (window.scrollY >= height * POPUP.afterHeroFactor) {
        window.removeEventListener("scroll", onScroll);
        remember();
        setOpen(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!products.length) return null;
  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" aria-labelledby="popup-title"
      PaperProps={{ sx: { borderRadius: "28px", background: tokens.cream, p: { xs: 2.5, md: 4 }, m: { xs: 1.5, md: 4 } } }}>
      <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: "absolute", top: 12, right: 12 }}><CloseIcon /></IconButton>
      <Typography id="popup-title" variant="h3" sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 300, pr: 5 }}>{POPUP.title}</Typography>
      <Typography sx={{ color: tokens.muted, mt: 0.75, mb: 3 }}>{POPUP.sub}</Typography>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
        {products.map((p) => (
          <Box key={p.id} sx={{ background: "#F3EEE6", borderRadius: "20px", p: 2, display: "flex", flexDirection: "column" }}>
            <Box sx={{ height: { xs: 90, sm: 130 }, background: "#E8E0D4", borderRadius: "14px", display: "grid", placeItems: "center", color: tokens.muted }}>
              <ImageOutlinedIcon sx={{ opacity: 0.7 }} />
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 17 }}>{p.name}</Typography>
            <Typography sx={{ fontSize: 14, color: tokens.muted, flex: 1, mt: 0.5 }}>{p.detail}</Typography>
            <Typography sx={{ mt: 1.5, fontSize: 18 }}>{money(p.priceCents)}</Typography>
            <Button fullWidth startIcon={added[p.id] ? <CheckIcon /> : <AddIcon />} disabled={Boolean(added[p.id])}
              onClick={() => { add({ id: p.id, name: p.name, price: p.priceCents / 100 }); setAdded({ ...added, [p.id]: true }); }}
              sx={{ ...pillSx.moss, mt: 1.5, py: 1, fontSize: 14.5, "&.Mui-disabled": { background: tokens.sage, color: tokens.mossBtn } }}>
              {added[p.id] ? "Added" : "Add to cart"}
            </Button>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography component={RouterLink} to={POPUP.seeAll.to} onClick={() => setOpen(false)} sx={{ color: tokens.ink, fontSize: 15, textDecoration: "underline" }}>{POPUP.seeAll.label}</Typography>
        <Button onClick={() => setOpen(false)} sx={{ color: tokens.muted }}>Keep browsing</Button>
      </Box>
    </Dialog>
  );
}
